// HelpAssistant/fabPersistence.js
//
// Per-user persistence for the floating Help launcher (FAB): where the user
// dragged it to, and whether they dismissed it.
//
// WHY localStorage keyed by user id (documented choice):
// This mirrors, deliberately and exactly, the pattern already established by
// `components/FirstRunIntro/persistence.js` — a per-user UI preference that has
// no backend field of its own, namespaced `iyuga.<feature>.v1.<userId>.<fact>`
// so multiple accounts on one browser never leak into each other. Adding a new
// backend profile field for a launcher's x/y is out of scope for this additive,
// staging-only change. Trade-off (same as FirstRunIntro's): it is per-device —
// a user who moves the FAB on desktop still sees the default position on their
// phone. A future cross-device upgrade is a `help_fab_prefs` profile field
// consumed here instead; the exported functions below are the only call sites.
//
// Two facts are stored, both scoped to the user id:
//   position  -> {left, top} in CSS px from the viewport's top-left, so the FAB
//                reappears exactly where the user dropped it.
//   dismissed -> the user hid the FAB with its X. While true the launcher does
//                not render at all, and the avatar/Profile menu grows a "Help"
//                entry that clears this flag (the ONLY way back).
//
// A change made in one component must be seen by the other (the FAB lives at
// the app root, the Profile menu lives in the header — they are siblings, not
// parent/child), so every writer notifies through a window CustomEvent and
// readers subscribe via `subscribeHelpFab`. The `storage` event is also
// subscribed to so a second tab stays in sync.

const NS = "iyuga.helpFab.v1";

/** Event name broadcast on every write, so sibling components re-read. */
export const HELP_FAB_EVENT = "iyuga:help-fab-changed";

export const positionKey = (userId) => `${NS}.${userId}.position`;
export const dismissedKey = (userId) => `${NS}.${userId}.dismissed`;

const safeGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    // Private-mode / storage-disabled: fail open (defaults) but never throw
    // into render.
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    /* storage unavailable — non-fatal */
  }
};

const safeRemove = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    /* storage unavailable — non-fatal */
  }
};

/** Broadcast that the FAB's persisted state changed (same-tab siblings). */
export const notifyHelpFabChanged = () => {
  try {
    window.dispatchEvent(new CustomEvent(HELP_FAB_EVENT));
  } catch (e) {
    /* no CustomEvent (very old browser) — subscribers fall back to `storage` */
  }
};

/**
 * Subscribe to FAB state changes. Returns an unsubscribe function.
 * Shape is `(cb) => () => void` so it can be handed straight to
 * `useSyncExternalStore`.
 */
export const subscribeHelpFab = (onChange) => {
  const handler = () => onChange();
  window.addEventListener(HELP_FAB_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(HELP_FAB_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
};

/**
 * The user's dragged position, or null when they have never moved it (→ the
 * caller uses the default corner). Malformed/partial JSON reads as null rather
 * than throwing or positioning the FAB at NaN.
 */
export const getFabPosition = (userId) => {
  if (userId == null) return null;
  const raw = safeGet(positionKey(userId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const left = Number(parsed?.left);
    const top = Number(parsed?.top);
    if (!Number.isFinite(left) || !Number.isFinite(top)) return null;
    return { left, top };
  } catch (e) {
    return null;
  }
};

/** Persist where the user dropped the FAB. Rounded — sub-pixel is noise here. */
export const setFabPosition = (userId, position) => {
  if (userId == null) return;
  const left = Number(position?.left);
  const top = Number(position?.top);
  if (!Number.isFinite(left) || !Number.isFinite(top)) return;
  safeSet(
    positionKey(userId),
    JSON.stringify({ left: Math.round(left), top: Math.round(top) })
  );
  notifyHelpFabChanged();
};

/** True once the user has hidden the FAB with its X. Default false (shown). */
export const isFabDismissed = (userId) => {
  if (userId == null) return false;
  return safeGet(dismissedKey(userId)) === "true";
};

/**
 * Hide (true) or restore (false) the FAB for this user.
 * Restoring REMOVES the key rather than writing "false", so the stored state
 * matches a never-dismissed user exactly (no lingering tombstone to reason
 * about later).
 */
export const setFabDismissed = (userId, dismissed) => {
  if (userId == null) return;
  if (dismissed) {
    safeSet(dismissedKey(userId), "true");
  } else {
    safeRemove(dismissedKey(userId));
  }
  notifyHelpFabChanged();
};
